import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "react-i18next";

/**
 * AgentScanner — premium loading VFX for the Auditor's deep/quick run.
 *
 *  - Outer ring: conic gradient (cyan #22D3EE → magenta #D946EF → cyan), 8s rotation.
 *  - Inner ring: opposite-direction conic gradient, 12s rotation.
 *  - Glow pulse: alternating cyan/magenta box-shadow, 1.5s.
 *  - Rotating agent text every 1.8s with vertical fade-in/out.
 */
export function AgentScanner() {
  const { t } = useTranslation();
  const messages = [
    t("auditor.scanner.maxx"),
    t("auditor.scanner.opus"),
    t("auditor.scanner.guard"),
  ];
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setIdx((i) => (i + 1) % messages.length);
    }, 1800);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length]);

  return (
    <div
      className="flex h-full min-h-[360px] flex-col items-center justify-center gap-8"
      data-testid="agent-scanner"
    >
      <div className="relative h-[220px] w-[220px]">
        {/* Glow pulse — outermost layer */}
        <motion.div
          aria-hidden
          className="absolute -inset-3 rounded-full"
          animate={{
            boxShadow: [
              "0 0 28px 6px rgba(34,211,238,0.45), 0 0 60px 18px rgba(34,211,238,0.18)",
              "0 0 28px 6px rgba(217,70,239,0.55), 0 0 60px 18px rgba(217,70,239,0.20)",
              "0 0 28px 6px rgba(34,211,238,0.45), 0 0 60px 18px rgba(34,211,238,0.18)",
            ],
          }}
          transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
        />

        {/* Outer rotating conic ring */}
        <motion.div
          aria-hidden
          className="absolute inset-0 rounded-full"
          style={{
            background:
              "conic-gradient(from 0deg, #22D3EE 0%, #D946EF 50%, #22D3EE 100%)",
            WebkitMaskImage:
              "radial-gradient(circle at center, transparent 86px, #000 90px, #000 108px, transparent 112px)",
            maskImage:
              "radial-gradient(circle at center, transparent 86px, #000 90px, #000 108px, transparent 112px)",
            filter: "drop-shadow(0 0 12px rgba(34,211,238,0.45))",
          }}
          animate={{ rotate: 360 }}
          transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
        />

        {/* Inner counter-rotating conic ring */}
        <motion.div
          aria-hidden
          className="absolute inset-[26px] rounded-full"
          style={{
            background:
              "conic-gradient(from 180deg, #D946EF 0%, #22D3EE 50%, #D946EF 100%)",
            WebkitMaskImage:
              "radial-gradient(circle at center, transparent 56px, #000 60px, #000 74px, transparent 78px)",
            maskImage:
              "radial-gradient(circle at center, transparent 56px, #000 60px, #000 74px, transparent 78px)",
            filter: "drop-shadow(0 0 8px rgba(217,70,239,0.55))",
          }}
          animate={{ rotate: -360 }}
          transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
        />

        {/* Center crosshair dot */}
        <div
          aria-hidden
          className="absolute inset-0 flex items-center justify-center"
        >
          <span
            className="h-2 w-2 rounded-full"
            style={{
              background: "#fff",
              boxShadow:
                "0 0 14px rgba(255,255,255,0.85), 0 0 28px rgba(217,70,239,0.55)",
            }}
          />
        </div>

        {/* Subtle scan line */}
        <motion.div
          aria-hidden
          className="absolute inset-0 rounded-full overflow-hidden"
          style={{
            WebkitMaskImage:
              "radial-gradient(circle at center, transparent 86px, #000 88px, #000 110px, transparent 112px)",
            maskImage:
              "radial-gradient(circle at center, transparent 86px, #000 88px, #000 110px, transparent 112px)",
          }}
        >
          <motion.div
            className="absolute inset-x-0 h-[2px] bg-white/70"
            style={{ filter: "drop-shadow(0 0 6px rgba(255,255,255,0.75))" }}
            initial={{ y: "0%" }}
            animate={{ y: ["0%", "100%", "0%"] }}
            transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}
          />
        </motion.div>
      </div>

      {/* Rotating agent text */}
      <div
        className="flex h-6 items-center justify-center"
        data-testid="agent-scanner-text"
      >
        <AnimatePresence mode="wait">
          <motion.span
            key={idx}
            initial={{ y: 8, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -8, opacity: 0 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            className="text-[11px] sm:text-xs uppercase tracking-[0.22em] font-medium text-white/85"
            style={{ textShadow: "0 0 8px rgba(255,255,255,0.35)" }}
          >
            {messages[idx]}
          </motion.span>
        </AnimatePresence>
      </div>
    </div>
  );
}

export default AgentScanner;
